import { useEffect, type ReactNode } from 'react'

import { useConsumerBranding } from '@/consumer/hooks/use-consumer-branding'
import { FormattingProvider, countryToLocale } from '@/shared/lib/formatting'
import { applyTenantTheme, brandingToTheme } from '@/shared/lib/theme'

type Props = {
  /** A tenant the page itself names (e.g. a scanned label). The host still wins. */
  slug?: string
  children: ReactNode
}

/**
 * Applies the tenant's design-token overrides for the consumer (Olive) app.
 * Unset tokens fall through to the Clara Vale baseline in `index.css`.
 */
export function ConsumerThemeProvider({ slug, children }: Props) {
  const { data } = useConsumerBranding(slug)
  const theme = data?.theme
  useEffect(() => {
    applyTenantTheme(brandingToTheme({ theme }))
  }, [theme])
  return <>{children}</>
}

/**
 * Feeds the shared `FormattingProvider` with the tenant's locale so dates and
 * currency render in the organisation's format rather than the browser
 * default. Falls back to the provider's platform defaults until branding
 * resolves.
 */
export function ConsumerFormattingProvider({ slug, children }: Props) {
  const { data } = useConsumerBranding(slug)
  const loc = data?.locale
  const locale = loc ? countryToLocale(loc.default_country_code, loc.default_language) : undefined
  const currency = loc?.default_currency || undefined
  const defaultCountryCode = loc?.default_country_code || undefined
  return (
    <FormattingProvider
      locale={locale}
      currency={currency}
      defaultCountryCode={defaultCountryCode}
      availableCurrencies={loc?.available_currencies}
    >
      {children}
    </FormattingProvider>
  )
}
